import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Tabs,
  Tab,
  Paper,
  Typography,
  Chip,
  Snackbar,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import HistoryIcon from '@mui/icons-material/History';
import { useThunkDispatch } from 'hooks';
import { Styles } from 'components/Tools';
import { createComisionist, updateComisionist, fetchComisionistDetail } from 'redux/actions/comisionist.actions';
import { CreateComisionistRequest } from 'types/api/responses';
import { ComisionistDataForm, ComisionistDataFormHandle } from './tabs/ComisionistDataForm';
import {
  comisionistDataInitialValues,
  getRequiredDocumentsByType,
} from './utils/comisionist.validation';
import { ComisionistDocumentsForm } from './tabs/ComisionistDocumentsForm';
import { ComisionistHistoryView } from './tabs/ComisionistHistoryView';
import {
  getPrimaryStatusLabel,
  getPrimaryStatusColor,
} from 'utils/statusTranslations';
import { ComisionistSecondaryStatusChip } from './ComisionistStatusChips';
import { RenderIf } from 'components/Tools/Utils';
import './comisionist.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`comisionist-tabpanel-${index}`}
      aria-labelledby={`comisionist-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

type Props = {
  onClose: () => void;
  onSuccess?: () => void;
  initialData?: CreateComisionistRequest | null;
  isLoadingData?: boolean;
};

export function NewComisionistScreen({ onClose, onSuccess, initialData, isLoadingData }: Props) {
  const classes = Styles();
  const dispatch = useThunkDispatch();

  const [tabValue, setTabValue] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' }
  );
  const [isEditMode, setIsEditMode] = useState(!!initialData?.name);

  const dataFormRef = useRef<ComisionistDataFormHandle>(null);
  const [formData, setFormData] = useState<CreateComisionistRequest>(comisionistDataInitialValues);

  const [documents, setDocuments] = useState<any[]>([]);

  // Callback para actualizar los estados en el header cuando sube/borra un documento
  const handleStatusChange = (primaryStatus: string, secondaryStatus: string) => {
    setFormData((prev) => ({
      ...prev,
      status: primaryStatus as any,
      documentsStatus: secondaryStatus as any,
    }));
  };

  // Cargar datos iniciales si existen
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setIsEditMode(true);

      const initialDocs: any[] = [];
      if (initialData.identityDoc) {
        initialDocs.push({ id: 'identity', type: 'DUI/NIT', fileName: 'Documento DUI/NIT', path: initialData.identityDoc, uploadedAt: 'Cargado' });
      }
      if (initialData.nrcDoc) {
        initialDocs.push({ id: 'nrc', type: 'NRC', fileName: 'Documento NRC', path: initialData.nrcDoc, uploadedAt: 'Cargado' });
      }
      if (initialData.signedFile) {
        initialDocs.push({ id: 'signedFile', type: 'Ficha Firmada', fileName: 'Ficha Firmada', path: initialData.signedFile, uploadedAt: 'Cargado' });
      }
      setDocuments(initialDocs);
    }
  }, [initialData]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
    // Limpiar mensajes al cambiar de tab
    setError(null);
    setSuccessMessage(null);
  };

  const validateDocuments = (): boolean => {
    const requiredDocs = getRequiredDocumentsByType(formData.type);
    const uploadedDocs = requiredDocs.filter((doc) =>
      documents.some((d) => d.type === doc),
    );

    if (uploadedDocs.length < requiredDocs.length) {
      const missing = requiredDocs.filter((doc) => !uploadedDocs.includes(doc));
      setError(
        `Documentos requeridos (${formData.type}): ${requiredDocs.join(', ')}. Faltan: ${missing.join(', ')}.`,
      );
      setTabValue(1);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (tabValue === 0) {
      const isDataValid = await dataFormRef.current?.validate();
      if (!isDataValid) {
        setError('Revise los campos del formulario de datos del comisionista');
        setTabValue(0);
        return;
      }
    }
    setError(null);

    if (isEditMode) {
      // Modo edición: PUT /commissioner/{clientAccount}
      const account = formData.clientAccount as string;
      if (!account) {
        setSnackbar({ open: true, message: 'No se encontró la cuenta del comisionista', severity: 'error' });
        return;
      }

      let payload = { ...formData };

      if (tabValue === 1) {
        if (!validateDocuments()) {
          return;
        }
        payload.documentsStatus = 'DocsOK';
      }

      setIsSubmitting(true);
      dispatch(
        updateComisionist(
          account,
          payload,
          () => {
            setIsSubmitting(false);
            setSnackbar({ open: true, message: 'Comisionista actualizado correctamente', severity: 'success' });
            if (tabValue === 1) {
              dispatch(fetchComisionistDetail(account));
            }
          },
          (msg) => {
            setIsSubmitting(false);
            setSnackbar({ open: true, message: msg, severity: 'error' });
          },
        ),
      );
      return;
    }

    // Modo creación
    if (!validateDocuments()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(createComisionist(formData));
      setSuccessMessage('Comisionista creado exitosamente');
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError('Error al crear el comisionista. Intente nuevamente.');
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const clientAccount = formData.clientAccount;
  const comisionistName = formData.name;

  return (
    <Box className="comisionist-screen">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1}
        bgcolor="#1a237e"
        color="white"
        boxShadow={1}
      >
        {/* Left: Volver + nombre/cuenta */}
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            style={{
              color: 'white',
              textTransform: 'none',
              minWidth: 'auto',
              paddingRight: '16px',
              borderRight: '1px solid rgba(255,255,255,0.3)',
              marginRight: '16px',
            }}
          >
            Volver
          </Button>
          <Box>
            <RenderIf condition={!!clientAccount}>
              <Typography
                variant="caption"
                style={{ color: 'rgba(255,255,255,0.75)', display: 'block', lineHeight: 1.2 }}
              >
                Cuenta: {clientAccount}
              </Typography>
            </RenderIf>
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                lineHeight: 1.3,
              }}
            >
              {comisionistName || (isEditMode ? 'Comisionista' : 'Crear Nuevo Comisionista')}
            </Typography>
          </Box>
        </Box>

        {/* Right: chips de estado */}
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <RenderIf condition={!!formData.status}>
            <Chip
              label={formData.status ? getPrimaryStatusLabel(formData.status) : ''}
              size="small"
              style={{
                backgroundColor: formData.status ? getPrimaryStatusColor(formData.status) : '#666',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.72rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            />
          </RenderIf>

            <ComisionistSecondaryStatusChip row comisionist={formData as any} />
        </Box>
      </Box>

      {/* Content */}
      <Box p={2} style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" style={{ marginBottom: '16px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" style={{ marginBottom: '16px' }}>
            {successMessage}
          </Alert>
        )}

        <Paper style={{ marginBottom: '16px' }}>
          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Tabs del formulario de comisionista"
            variant="fullWidth"
            style={{ borderBottom: '1px solid #e0e0e0' }}
          >
            <Tab
              label={<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><PersonIcon fontSize="small" /> Datos del Comisionista</span>}
              id="comisionist-tab-0"
              aria-controls="comisionist-tabpanel-0"
            />
            <Tab
              label={<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FolderIcon fontSize="small" /> Documentos Digitales</span>}
              id="comisionist-tab-1"
              aria-controls="comisionist-tabpanel-1"
            />
            <Tab
              label={<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><HistoryIcon fontSize="small" /> Historial</span>}
              id="comisionist-tab-2"
              aria-controls="comisionist-tabpanel-2"
            />
          </Tabs>

          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            <ComisionistDataForm
              ref={dataFormRef}
              initialValues={formData}
              onValuesChange={setFormData}
              isSubmitting={isSubmitting}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <ComisionistDocumentsForm
              formData={formData}
              documents={documents}
              onDocumentsChange={setDocuments}
              onStatusChange={handleStatusChange}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <ComisionistHistoryView
              comisionistId={null}
              clientAccount={formData.clientAccount ?? null}
              isNewRecord={!formData.clientAccount}
            />
          </TabPanel>
        </Paper>
      </Box>

      <RenderIf condition={tabValue !== 2}>
        {/* Footer Actions */}
        <Box
          display="flex"
          justifyContent="flex-end"
          p={2}
          bgcolor="#f5f5f5"
          borderTop="1px solid #e0e0e0"
          style={{ gap: '8px' }}
        >
          {tabValue === 1 ? (
            /* Tab Documentos Digitales */
            <>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
                startIcon={<ArrowBackIcon />}
              >
                Volver a Bandeja
              </Button>
            </>
          ) : (
            /* Tab Datos del Comisionista */
            <>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
      {/*         <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled
              >
                Guardar Comisionista
              </Button> */}
            </>
          )}
        </Box>
      </RenderIf>

      {/* Toast notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
